import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './BundleDetailsModal.scss'
import Modal from '../../../../components/Modal'
import Button, { ButtonTheme } from '../../../../components/Form/Button'
import DateUtils from '../../../../utils/date'
import { DATE_FORMAT } from '../../../../constant'
import BundleReleaseModal from '../BundleReleaseModal/BundleReleaseModal'
import BundlesService from '../../services/bundles.service'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import { bundleActions } from '../../store/bundle-slice'
import FillingSpinner, { FillingSpinnerType } from '../../../../components/FillingSpinner'
import BundleLogs from '../../components/BundleLogs/BundleLogs'
import BundleSchedulesTable from '../../components/BundleSchedulesTable/BundleSchedulesTable'
import { BundleExtendedModel } from '../../../../api/bundle/models/bundle-extended.model'
import { BundleScheduleModel } from '../../../../api/bundle/models/bundle-schedule.model'
import { AppContext } from '../../../../contexts/AppContext'
import { MediaFormat } from '../../../../types/campaign'

const BundleDetailsModal: React.FC = () => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const dispatch = useDispatch()
  const [showReleaseModal, setShowReleaseModal] = React.useState(false)

  const { selectedBundle, bundleHistory, filters, pageNumber } = useSelector(
    (state: RootState) => state.bundles
  )

  useEffect(() => {
    if (!selectedBundle) {
      return
    }

    void BundlesService.fetchBundleHistory(selectedBundle.id)
  }, [selectedBundle])

  const closeClick = () => {
    void dispatch(bundleActions.setSelectedBundle(undefined))
  }

  const releaseAvailable = (selectedBundle: BundleExtendedModel | undefined) => {
    if (!selectedBundle) {
      return false
    }

    const isAnyScheduleUnassigned = selectedBundle.bundleSchedules.some(
      (schedule: BundleScheduleModel) => schedule.status === 'unassigned'
    )

    return isAnyScheduleUnassigned
  }

  const releaseSchedule = (schedule: BundleScheduleModel) => {
    void BundlesService.releaseBundleSchedule(schedule.id, addNotification, t, filters, pageNumber)
  }

  return (
    <Modal
      title={`${t('bundle.detailsTitle')}: ${selectedBundle?.id}`}
      classNameOverlay='BundleDetailsModal'
      isOpen={!!selectedBundle}
      onClose={() => void closeClick()}
      footer={
        <div className={'BundleDetailsModal__footer'}>
          <Button
            theme={ButtonTheme.BLUE}
            onClick={() => setShowReleaseModal(true)}
            disabled={!releaseAvailable(selectedBundle)}
          >
            {t('bundle.release')}
          </Button>
        </div>
      }
    >
      {selectedBundle && (
        <>
          <div className={'BundleDetailsModal__container'}>
            <div className={'BundleDetailsModal__details-container'}>
              <p>{t<string>('bundle.id')}:</p>
              <p>{selectedBundle.id}</p>
              <p>{t<string>('bundle.identifier')}:</p>
              <p>{selectedBundle.identifier ?? '-'}</p>
              <p>{t<string>('bundle.commercialName')}:</p>
              <p>{selectedBundle.commercialName}</p>
              <p>{t<string>('bundle.mediaFormat')}:</p>
              <p>
                {t(
                  `mediaType.${MediaFormat[selectedBundle.mediaFormat as keyof typeof MediaFormat]}`
                ) ?? '-'}
              </p>
              <p>{t<string>('bundle.startDate')}:</p>
              <p>{DateUtils.parseAndFormat(selectedBundle.startDate, DATE_FORMAT)}</p>
              <p>{t<string>('bundle.endDate')}:</p>
              <p>{DateUtils.parseAndFormat(selectedBundle.endDate, DATE_FORMAT)}</p>
              <p>{t<string>('bundle.schedule')}:</p>
            </div>

            <div className={'BundleDetailsModal__bundle-container'}>
              <BundleSchedulesTable
                data={selectedBundle.bundleSchedules}
                releaseScheduleClick={releaseSchedule}
              />
            </div>

            {bundleHistory.loading ? (
              <FillingSpinner type={FillingSpinnerType.STANDARD} />
            ) : (
              <BundleLogs data={bundleHistory.data} />
            )}
          </div>
          <BundleReleaseModal
            bundle={selectedBundle}
            isModalOpen={showReleaseModal}
            setModalOpen={setShowReleaseModal}
            filters={filters}
            pageNumber={pageNumber}
          />
        </>
      )}
    </Modal>
  )
}

export default BundleDetailsModal
